/**
 * Re-issuing a document as a fresh invoice.
 *
 * A retainer is billed every month with an identical line item, while the
 * commission based position carries a different amount each time. So the only
 * thing worth asking for is the price — recipient, discounts, tax, sender and
 * layout are carried over unchanged and the dates start over from today.
 */

import { calculateOfferPricing, getEffectiveServicePrice } from "./offerPricing";
import { PAYMENT_TYPES, getPaymentType } from "../OfferLanguage";

export const DEFAULT_PAYMENT_TERM_DAYS = 7;
export const PAYMENT_TERM_PRESETS = [7, 14, 30];

const MS_PER_DAY = 86400000;
// Timestamps sit at midday so a timezone shift can never move a document onto
// the previous or next day.
const NOON = 12;

export const toDateInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const dateInputToTimestamp = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day, NOON);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

export const shiftDateInput = (value, days) => {
  const timestamp = dateInputToTimestamp(value);
  if (timestamp === null) return "";
  const date = new Date(timestamp);
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
};

export const daysBetweenDateInputs = (from, to) => {
  const start = dateInputToTimestamp(from);
  const end = dateInputToTimestamp(to);
  if (start === null || end === null) return null;
  return Math.round((end - start) / MS_PER_DAY);
};

/**
 * Payment term the source document was issued with, so a re-issued invoice
 * keeps the term agreed with that customer instead of falling back to the
 * default every time. On an offer the same field holds the validity date
 * instead of a due date, which says nothing about the payment term.
 */
export const derivePaymentTermDays = (offer) => {
  if (offer?.documentType !== "invoice") return DEFAULT_PAYMENT_TERM_DAYS;

  const issued = offer?.invoiceDate || toDateInputValue(offer?.createdAt);
  const days = daysBetweenDateInputs(issued, offer?.offerValidTill);
  if (days === null || days <= 0 || days > 365) return DEFAULT_PAYMENT_TERM_DAYS;
  return days;
};

// Same precedence as the offer editor: a per-document paymentType wins, then a
// per-document legacy flag, then whatever the catalog service says.
const resolvePaymentType = (service, override = {}) => {
  if (override.paymentType) return override.paymentType;
  if (override.isRecurring !== undefined) {
    return override.isRecurring ? PAYMENT_TYPES.RECURRING : PAYMENT_TYPES.ONE_TIME;
  }
  return getPaymentType(service);
};

/**
 * Line items of a document in their stored order, resolved against the service
 * catalog. Services that were deleted from the catalog are flagged instead of
 * dropped silently, because they no longer count towards the total.
 */
export const resolveOfferLines = (offer, catalogServices = []) => {
  const byId = new Map(catalogServices.map((service) => [service.id, service]));

  return (offer?.services || []).map((serviceId) => {
    const service = byId.get(serviceId) || null;
    const override = offer?.serviceOverrides?.[serviceId] || {};

    return {
      id: serviceId,
      service,
      isMissing: !service,
      title: override.title || service?.title || "Deleted service",
      discountPercent: service?.discountPercent || 0,
      paymentType: service ? resolvePaymentType(service, override) : null,
      catalogPrice: service?.price || 0,
      price: service ? getEffectiveServicePrice(offer, service) : 0,
    };
  });
};

// Firebase rejects `undefined`, and the open editor keeps cleared overrides
// around as `{ price: undefined }` until the next save.
const stripUndefined = (value) => {
  if (Array.isArray(value)) return value.map(stripUndefined);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((clean, [key, entry]) => {
      if (entry === undefined) return clean;
      clean[key] = stripUndefined(entry);
      return clean;
    }, {});
  }
  return value;
};

/**
 * Builds the document to write to `/World/{userId}/offers`.
 *
 * @param {object} source document being re-issued (offer or invoice)
 * @param {Array} lines result of resolveOfferLines
 * @param {object} prices new price per service id
 */
export const buildReissuedInvoice = ({
  source,
  lines = [],
  prices = {},
  invoiceNumber,
  invoiceDate,
  dueDate,
  customerName,
  customerAddress,
}) => {
  // Leftovers from services that were removed from the document at some point
  // would travel from copy to copy forever.
  const serviceOverrides = Object.entries(source?.serviceOverrides || {}).reduce(
    (kept, [serviceId, override]) => {
      const remaining = stripUndefined(override || {});
      if ((source?.services || []).includes(serviceId) && Object.keys(remaining).length) {
        kept[serviceId] = remaining;
      }
      return kept;
    },
    {}
  );

  lines.forEach((line) => {
    const price = prices[line.id];
    // A price cannot be re-entered for a service that no longer exists, so its
    // stored override is left alone in case the service comes back.
    if (line.isMissing || price === undefined || price === null) return;

    const override = { ...(serviceOverrides[line.id] || {}) };
    // Back at the catalog price there is nothing left to override.
    if (line.service && price === line.catalogPrice) delete override.price;
    else override.price = price;

    const remaining = stripUndefined(override);
    if (Object.keys(remaining).length) serviceOverrides[line.id] = remaining;
    else delete serviceOverrides[line.id];
  });

  const draft = stripUndefined({
    ...source,
    documentType: "invoice",
    invoiceNumber: invoiceNumber || "",
    invoiceDate: invoiceDate || "",
    offerValidTill: dueDate || "",
    serviceOverrides,
    customerName: customerName ?? source?.customerName ?? "",
    customerAddress: customerAddress ?? source?.customerAddress ?? "",
    createdAt: dateInputToTimestamp(invoiceDate) ?? Date.now(),
    updatedAt: Date.now(),
    // A copy must never inherit the share link of the original.
    isPublic: false,
    // An offer turned into an invoice would otherwise print its offer number.
    showOfferNumber: false,
  });

  delete draft.id;
  delete draft.type;

  // Totals are denormalised onto the document (the dashboard sorts and renders
  // from them), so they have to be recalculated rather than copied along.
  const services = lines
    .filter((line) => line.service)
    .map((line) => line.service);

  return { ...draft, ...calculateOfferPricing(draft, services) };
};
